import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, MessageCircle, Search, UsersRound } from "lucide-react";
import { alertSourceUrl, boardGreetingName } from "@/lib/board-presentation";
import { SavedFacilityButton } from "@/components/board/SavedFacilityButton";
import { useAuth } from "@/contexts/AuthContext";
import { useCity } from "@/contexts/CityContext";
import { acceptingLabel, waitBucketLabel } from "@/lib/availability";
import { track } from "@/lib/analytics";
import { formatMessageRelativeTime } from "@/lib/messages";
import {
  formatWeekInRoundsLine,
  suggestHandle,
  useBoardHome,
  useBoardMe,
  useSavedFacilities,
  useUpdateBoardMe,
} from "@/lib/board";

const DEFAULT_INTERESTS = [
  "Cardiology",
  "Dermatology",
  "Endocrinology",
  "Gastroenterology",
  "Neurology",
  "Orthopaedics",
  "Paediatrics",
  "Psychiatry",
] as const;

export default function BoardHome() {
  const { city } = useCity();
  const home = useBoardHome(city.slug);
  const saved = useSavedFacilities(city.slug);
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [allAlerts, setAllAlerts] = useState(false);

  useEffect(() => track("board_view"), []);

  if (home.isLoading) return <BoardState>Loading your Board…</BoardState>;
  if (home.isError || !home.data) {
    return (
      <BoardState error>
        <strong>Your Board could not be loaded.</strong>
        <button type="button" onClick={() => void home.refetch()}>Retry</button>
      </BoardState>
    );
  }

  const data = home.data;
  const weekInRounds = data.weekInRounds
    ? formatWeekInRoundsLine(data.weekInRounds)
    : "";
  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const query = search.trim();
    track("search", { query_length: query.length, specialty: "" });
    navigate(`/directory?type=specialist${query ? `&q=${encodeURIComponent(query)}` : ""}`);
  };

  return (
    <div className="pb-board-home">
      <header className="pb-board-page-heading">
        <div>
          <p className="pb-board-eyebrow">{city.name} clinician cockpit</p>
          <h1>Good morning{data.member.displayName ? `, ${boardGreetingName(data.member.displayName)}` : ""}.</h1>
        </div>
        <span>{formatLongDate(new Date())}</span>
      </header>

      {!data.member.onboarded ? <OnboardingCard /> : null}

      <section className="pb-board-cpd-hero" aria-labelledby="cpd-hero-heading">
        <header className="pb-board-cpd-hero-heading">
          <div><p className="pb-board-eyebrow">Specialist insight. Everyday practice.</p><h2 id="cpd-hero-heading">Five Minute CPD</h2></div>
          <Link to="/board/learn">Browse all sessions <ArrowRight size={18} aria-hidden="true" /></Link>
        </header>
        {data.beforeYouLeave.tile ? (
          <div className="pb-board-cpd-hero-body">
            <Link className="pb-board-cpd-preview" to={`/board/learn/${data.beforeYouLeave.tile.id}`} aria-label={`Open ${data.beforeYouLeave.tile.title}`}>
              <span className="pb-board-cpd-preview-mark" aria-hidden="true">5<span>MINUTE<br />CPD</span></span>
              <span className="pb-board-cpd-preview-action">Explore this session <ArrowRight size={20} aria-hidden="true" /></span>
              <span className="pb-board-cpd-duration">{data.beforeYouLeave.tile.minutes} min</span>
            </Link>
            <div className="pb-board-cpd-hero-copy">
              <p className="pb-board-eyebrow">Featured · {data.beforeYouLeave.tile.specialtyName}</p>
              <h3>{data.beforeYouLeave.tile.title}</h3>
              <p className="pb-board-cpd-speaker">{data.beforeYouLeave.tile.specialistName}</p>
              <p>Make a little room for practical learning, then keep your notes and actual learning time together.</p>
              <Link className="pb-board-primary" to={`/board/learn/${data.beforeYouLeave.tile.id}`}>Open session <ArrowRight size={18} aria-hidden="true" /></Link>
              <Link className="pb-board-cpd-record-link" to="/board/cpd">My learning record</Link>
            </div>
          </div>
        ) : (
          <div className="pb-board-cpd-hero-empty"><h3>Your next learning break starts here.</h3><p>Specialist sessions will appear here when published.</p><Link className="pb-board-primary" to="/board/learn">Explore the library <ArrowRight size={18} aria-hidden="true" /></Link></div>
        )}
      </section>

      <section className="pb-board-ritual" aria-labelledby="good-morning-heading">
        <SectionHeading
          label="01"
          title="Good Morning"
          detail="Current alerts and recent network activity"
          id="good-morning-heading"
        />
        <div className="pb-board-two-col">
          <BoardCard title="Alerts">
            {data.goodMorning.alerts.length ? (
              <>
              <ul className="pb-board-list" id="board-alert-list">
                {data.goodMorning.alerts.slice(0, allAlerts ? undefined : 2).map((alert) => (
                  <li key={alert.id}>
                    <span className="pb-board-level" data-level={alert.level}>Level {alert.level}</span>
                    <div>
                      <strong>{alert.title}</strong>
                      <small>{formatRelativeDate(alert.publishedAt)}</small>
                    </div>
                    {alertSourceUrl(alert.sourceUrl) ? <a href={alertSourceUrl(alert.sourceUrl)!} target="_blank" rel="noopener noreferrer">Source<span className="sr-only"> for {alert.title}</span></a> : <span className="pb-board-source-unavailable">Source unavailable</span>}
                  </li>
                ))}
              </ul>
              {data.goodMorning.alerts.length > 2 ? <button type="button" className="pb-board-alert-toggle" aria-controls="board-alert-list" aria-expanded={allAlerts} onClick={() => setAllAlerts(value => !value)}>{allAlerts ? "Show fewer alerts" : `Show all ${data.goodMorning.alerts.length} alerts`}</button> : null}
              </>
            ) : (
              <EmptyLine>No new alerts since your last visit.</EmptyLine>
            )}
          </BoardCard>

          <BoardCard title="Availability changes">
            {data.goodMorning.changes.length ? (
              <ul className="pb-board-list">
                {data.goodMorning.changes.map((change) => (
                  <li key={`${change.facilityId}-${change.changedAt}`}>
                    <AvailabilityPill value={change.accepting} />
                    <div>
                      <strong>{change.name}</strong>
                      <small>
                        {change.specialty}{change.practice ? ` · ${change.practice}` : ""}
                        {waitBucketLabel(change.waitBucket) ? ` · ${waitBucketLabel(change.waitBucket)}` : ""}
                      </small>
                    </div>
                    <Link to={`/board/referrals?facility=${encodeURIComponent(change.facilitySlug)}`}>Refer</Link>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyLine>No new availability changes.</EmptyLine>
            )}
          </BoardCard>

          <BoardCard title="Messages">
            <div className="pb-board-message-summary">
              <Link to="/board/messages">
                <span>{data.messages?.unread ?? 0} new {(data.messages?.unread ?? 0) === 1 ? "message" : "messages"}</span>
                <MessageCircle size={16} aria-hidden="true" />
              </Link>
              {data.messages?.latest.length ? (
                <ul>
                  {data.messages.latest.slice(0, 3).map((message) => (
                    <li key={message.conversationId}>
                      <Link to={`/board/messages/${encodeURIComponent(message.conversationId)}`}>
                        <strong>{message.from.display} · @{message.from.handle}</strong>
                        <time dateTime={message.at}>{formatMessageRelativeTime(message.at)}</time>
                        <small>{message.excerpt}</small>
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </BoardCard>
        </div>
        {weekInRounds ? (
          <Link className="pb-board-week-in-rounds" to="/board/rounds">
            <UsersRound size={15} aria-hidden="true" />
            <strong>This week in Rounds</strong>
            <span>{weekInRounds}</span>
            <ArrowRight size={14} aria-hidden="true" />
          </Link>
        ) : null}
        <p className="pb-board-joined">
          <UsersRound size={15} aria-hidden="true" />
          {data.goodMorning.joinedThisWeek} clinician{data.goodMorning.joinedThisWeek === 1 ? "" : "s"} joined this week
        </p>
      </section>

      <section className="pb-board-ritual" aria-labelledby="next-patient-heading">
        <SectionHeading
          label="02"
          title="Before the next patient"
          detail={`${data.nextPatient.acceptingCount} specialist${data.nextPatient.acceptingCount === 1 ? "" : "s"} accepting referrals`}
          id="next-patient-heading"
        />
        <form className="pb-board-search" onSubmit={submitSearch}>
          <Search size={17} aria-hidden="true" />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search specialist, specialty or suburb"
            aria-label="Search the specialist directory"
          />
          <button type="submit">Search directory</button>
        </form>
        <div className="pb-board-two-col">
          <BoardCard title="Recently confirmed">
            {data.nextPatient.recentlyConfirmed.length ? (
              <ul className="pb-board-list">
                {data.nextPatient.recentlyConfirmed.map((item) => (
                  <li key={`${item.facilitySlug}-${item.confirmedAt}`}>
                    <AvailabilityPill value={item.accepting} />
                    <div>
                      <strong>{item.name}</strong>
                      <small>{item.specialty}{waitBucketLabel(item.waitBucket) ? ` · ${waitBucketLabel(item.waitBucket)}` : ""}</small>
                    </div>
                    <Link to={`/board/referrals?facility=${encodeURIComponent(item.facilitySlug)}`}>Refer</Link>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyLine>No recently confirmed availability.</EmptyLine>
            )}
          </BoardCard>

          <BoardCard title="Saved specialists">
            {saved.isLoading ? (
              <EmptyLine>Loading saved specialists…</EmptyLine>
            ) : saved.isError ? (
              <EmptyLine>Saved specialists are temporarily unavailable.</EmptyLine>
            ) : saved.data?.items.length ? (
              <ul className="pb-board-saved-list">
                {saved.data.items.map((facility) => (
                  <li key={facility.id}>
                    <div>
                      <Link to={`/directory/specialist/${facility.slug}`}><strong>{facility.name}</strong></Link>
                      <small>{facility.address_json.specialty ?? facility.address_json.practice_name ?? "Specialist"}</small>
                    </div>
                    <SavedFacilityButton facility={facility} compact />
                    <div className="pb-board-saved-actions">
                      {facility.memberUserId ? (
                        <Link to={`/board/messages/new?to=${encodeURIComponent(facility.memberUserId)}`}>
                          <MessageCircle size={13} aria-hidden="true" />Message
                        </Link>
                      ) : null}
                      <Link to={`/board/referrals?facility=${encodeURIComponent(facility.slug)}`} aria-label={`Refer to ${facility.name}`}>
                        <ArrowRight size={15} aria-hidden="true" />
                      </Link>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyLine>
                Save specialists from the <Link to="/directory?type=specialist">directory</Link> for faster access here.
              </EmptyLine>
            )}
          </BoardCard>
        </div>
      </section>

      <section className="pb-board-ritual" aria-labelledby="before-leave-heading">
        <SectionHeading label="03" title="Before you leave" detail="Learn for five minutes and keep your CPD log current" id="before-leave-heading" />
        <div className="pb-board-two-col">
          <BoardCard title="CPD logged">
            <div className="pb-board-metrics">
              <Link to="/board/cpd"><strong>{data.beforeYouLeave.cpdMinutesThisMonth}</strong><span>minutes this month</span></Link>
              <Link to="/board/cpd"><strong>{data.beforeYouLeave.cpdMinutesThisYear}</strong><span>minutes this year</span></Link>
            </div>
          </BoardCard>
          <BoardCard title="Rounds">
            <Link className="pb-board-feature-link" to="/board/rounds">
              <UsersRound size={18} aria-hidden="true" />
              <span><strong>Local clinical discussion</strong><small>Private discussion with registered clinicians</small></span>
              <b>{data.beforeYouLeave.roundsUnread} unread</b>
            </Link>
          </BoardCard>
        </div>
      </section>
    </div>
  );
}

function OnboardingCard() {
  const { city } = useCity();
  const { user } = useAuth();
  const me = useBoardMe(city.slug);
  const update = useUpdateBoardMe(city.slug);
  const initialName = me.data?.displayName || user?.name || "";
  const [displayName, setDisplayName] = useState(initialName);
  const [handle, setHandle] = useState(suggestHandle(initialName));
  const [interests, setInterests] = useState<string[]>(me.data?.specialtyInterests ?? []);
  const [validation, setValidation] = useState<string | null>(null);

  const initialised = useRef(false);
  useEffect(() => {
    if (!me.data || initialised.current) return;
    initialised.current = true;
    setDisplayName(me.data.displayName || user?.name || "");
    setHandle(me.data.handle || suggestHandle(me.data.displayName || user?.name || ""));
    setInterests(me.data.specialtyInterests);
  }, [me.data, user?.name]);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!displayName.trim()) return setValidation("Add the name colleagues should see.");
    if (!/^[a-z0-9-]{3,24}$/.test(handle)) {
      return setValidation("Use 3–24 lowercase letters, numbers or hyphens for your handle.");
    }
    if (!interests.length) return setValidation("Choose at least one specialty interest.");
    setValidation(null);
    update.mutate({ displayName: displayName.trim(), handle, specialtyInterests: interests });
  };

  return (
    <aside className="pb-board-onboarding" aria-labelledby="onboarding-heading">
      <div>
        <p className="pb-board-eyebrow">One-minute setup</p>
        <h2 id="onboarding-heading">Make the Board yours</h2>
        <p>Choose your learning interests and how colleagues see you in Rounds and Messages.</p>
      </div>
      <form onSubmit={submit}>
        <label>Display name<input required value={displayName} onChange={(event) => setDisplayName(event.target.value)} /></label>
        <label>Handle<span className="pb-board-input-prefix">@</span><input required minLength={3} maxLength={24} pattern="[a-z0-9-]{3,24}" value={handle} onChange={(event) => setHandle(event.target.value.toLowerCase())} /></label>
        <fieldset>
          <legend>Specialty interests</legend>
          <div className="pb-board-chip-picker">
            {DEFAULT_INTERESTS.map((interest) => (
              <button
                key={interest}
                type="button"
                aria-pressed={interests.includes(interest)}
                onClick={() => setInterests((current) => current.includes(interest) ? current.filter((item) => item !== interest) : [...current, interest])}
              >
                {interest}
              </button>
            ))}
          </div>
        </fieldset>
        <button className="pb-board-primary" type="submit" disabled={update.isPending}>{update.isPending ? "Saving…" : "Finish setup"}</button>
        {validation ? <p className="pb-board-form-error" role="alert">{validation}</p> : null}
        {update.isError ? <p className="pb-board-form-error" role="alert">Those details could not be saved. The handle may already be in use.</p> : null}
      </form>
    </aside>
  );
}

function SectionHeading({ label, title, detail, id }: { label: string; title: string; detail: string; id: string }) {
  return <header className="pb-board-section-heading"><span>{label}</span><div><h2 id={id}>{title}</h2><p>{detail}</p></div></header>;
}

function BoardCard({ title, children }: { title: string; children: React.ReactNode }) {
  return <article className="pb-board-card"><h3>{title}</h3>{children}</article>;
}

function BoardState({ children, error = false }: { children: React.ReactNode; error?: boolean }) {
  return <div className={`pb-board-state${error ? " pb-board-state--error" : ""}`} role={error ? "alert" : "status"}>{children}</div>;
}

function EmptyLine({ children }: { children: React.ReactNode }) {
  return <p className="pb-board-empty">{children}</p>;
}

function AvailabilityPill({ value }: { value: Parameters<typeof acceptingLabel>[0] }) {
  return <span className="pb-board-availability" data-status={value}>{acceptingLabel(value)}</span>;
}

function formatLongDate(date: Date): string {
  return new Intl.DateTimeFormat("en-AU", { weekday: "long", day: "numeric", month: "long" }).format(date);
}

function formatRelativeDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-AU", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }).format(date);
}
